export const FIT_TYPE = {
  LINEAR: 'linear',
  LOG: 'log',
  POWER: 'power',
  QUADRATIC: 'quadratic',
};

export const FIT_TYPE_META = {
  [FIT_TYPE.LINEAR]: { label: 'Linear' },
  [FIT_TYPE.LOG]: { label: 'Log' },
  [FIT_TYPE.POWER]: { label: 'Power' },
  [FIT_TYPE.QUADRATIC]: { label: 'Quadratic' },
};

export const SPEND_SCALE = 1000000;

export function parameterCount(fitType) {
  return fitType === FIT_TYPE.QUADRATIC ? 3 : 2;
}

function solveLinearSystem(matrix, vector) {
  const size = vector.length;
  const rows = matrix.map((row, index) => [...row, vector[index]]);

  for (let column = 0; column < size; column += 1) {
    let pivot = column;
    for (let row = column + 1; row < size; row += 1) {
      if (Math.abs(rows[row][column]) > Math.abs(rows[pivot][column])) {
        pivot = row;
      }
    }
    if (Math.abs(rows[pivot][column]) < 1e-12) {
      return null;
    }
    if (pivot !== column) {
      const swap = rows[column];
      rows[column] = rows[pivot];
      rows[pivot] = swap;
    }
    const divisor = rows[column][column];
    for (let columnIndex = column; columnIndex <= size; columnIndex += 1) {
      rows[column][columnIndex] /= divisor;
    }
    for (let row = 0; row < size; row += 1) {
      if (row === column) {
        continue;
      }
      const factor = rows[row][column];
      for (let columnIndex = column; columnIndex <= size; columnIndex += 1) {
        rows[row][columnIndex] -= factor * rows[column][columnIndex];
      }
    }
  }

  return rows.map((row) => row[size]);
}

function designRow(fitType, scaledSpend) {
  if (fitType === FIT_TYPE.LOG || fitType === FIT_TYPE.POWER) {
    return [1, Math.log(scaledSpend)];
  }
  if (fitType === FIT_TYPE.QUADRATIC) {
    return [1, scaledSpend, scaledSpend * scaledSpend];
  }
  return [1, scaledSpend];
}

function dot(left, right) {
  return left.reduce((sum, value, index) => sum + value * right[index], 0);
}

export function fitCurve(fitType, points) {
  const usable = (points || []).filter(
    (point) =>
      point &&
      point.x > 0 &&
      point.y > 0 &&
      Number.isFinite(point.x) &&
      Number.isFinite(point.y)
  );
  const count = parameterCount(fitType);
  if (usable.length < count) {
    return null;
  }

  const design = usable.map((point) => designRow(fitType, point.x));
  const target = usable.map((point) =>
    fitType === FIT_TYPE.POWER ? Math.log(point.y) : point.y
  );
  const normal = Array.from({ length: count }, () => Array(count).fill(0));
  const normalTarget = Array(count).fill(0);

  design.forEach((row, index) => {
    for (let left = 0; left < count; left += 1) {
      normalTarget[left] += row[left] * target[index];
      for (let right = 0; right < count; right += 1) {
        normal[left][right] += row[left] * row[right];
      }
    }
  });

  const beta = solveLinearSystem(normal, normalTarget);
  if (!beta || beta.some((value) => !Number.isFinite(value))) {
    return null;
  }

  const fitted = design.map((row) => dot(row, beta));
  const mean = target.reduce((sum, value) => sum + value, 0) / target.length;
  const totalSumOfSquares = target.reduce(
    (sum, value) => sum + (value - mean) * (value - mean),
    0
  );
  const residualSumOfSquares = target.reduce(
    (sum, value, index) => sum + (value - fitted[index]) * (value - fitted[index]),
    0
  );
  const r2 =
    totalSumOfSquares === 0 ? 1 : 1 - residualSumOfSquares / totalSumOfSquares;

  const coefficients = { a: beta[0], b: beta[1] };
  if (count === 3) {
    coefficients.c = beta[2];
  }

  return {
    fitType,
    coefficients,
    r2,
    n: usable.length,
    evaluate(scaledSpend) {
      if (!(scaledSpend > 0) || !Number.isFinite(scaledSpend)) {
        return null;
      }
      if (fitType === FIT_TYPE.POWER) {
        return Math.exp(coefficients.a + coefficients.b * Math.log(scaledSpend));
      }
      if (fitType === FIT_TYPE.LOG) {
        return coefficients.a + coefficients.b * Math.log(scaledSpend);
      }
      if (fitType === FIT_TYPE.QUADRATIC) {
        return (
          coefficients.a +
          coefficients.b * scaledSpend +
          coefficients.c * scaledSpend * scaledSpend
        );
      }
      return coefficients.a + coefficients.b * scaledSpend;
    },
  };
}

export function residualSampleStd(points, evaluate) {
  const residuals = (points || [])
    .map((point) => {
      const fitted = evaluate(point.x);
      if (!Number.isFinite(fitted) || !Number.isFinite(point.y)) {
        return null;
      }
      return point.y - fitted;
    })
    .filter((residual) => residual != null);

  if (residuals.length < 3) {
    return null;
  }

  const mean = residuals.reduce((sum, residual) => sum + residual, 0) / residuals.length;
  const sumOfSquares = residuals.reduce(
    (sum, residual) => sum + (residual - mean) * (residual - mean),
    0
  );
  return Math.sqrt(sumOfSquares / (residuals.length - 1));
}

export function spendAtTarget(fit, targetY) {
  if (!fit || !(targetY > 0) || !Number.isFinite(targetY)) {
    return null;
  }

  const { a, b, c } = fit.coefficients;
  let scaledSpend = null;

  if (fit.fitType === FIT_TYPE.LINEAR) {
    scaledSpend = b ? (targetY - a) / b : null;
  } else if (fit.fitType === FIT_TYPE.LOG) {
    scaledSpend = b ? Math.exp((targetY - a) / b) : null;
  } else if (fit.fitType === FIT_TYPE.POWER) {
    scaledSpend = b ? Math.exp((Math.log(targetY) - a) / b) : null;
  } else if (fit.fitType === FIT_TYPE.QUADRATIC) {
    if (Math.abs(c || 0) < 1e-12) {
      scaledSpend = b ? (targetY - a) / b : null;
    } else {
      const discriminant = b * b - 4 * c * (a - targetY);
      if (discriminant < 0) {
        return null;
      }
      const root = Math.sqrt(discriminant);
      const candidates = [(-b + root) / (2 * c), (-b - root) / (2 * c)].filter(
        (value) => value > 0 && Number.isFinite(value)
      );
      scaledSpend = candidates.sort((left, right) => left - right)[0] ?? null;
    }
  }

  if (!(scaledSpend > 0) || !Number.isFinite(scaledSpend)) {
    return null;
  }
  return scaledSpend * SPEND_SCALE;
}
